import type { ToolCallLike } from '@/lib/assistant-tool-metadata'

function parseToolJson(result: string | undefined): Record<string, unknown> | null {
	if (!result) return null
	try {
		const parsed = JSON.parse(result)
		return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
	} catch {
		return null
	}
}

interface ComparisonPeriod {
	from: string
	to: string
	until?: string
	revenue_dollars?: number
	orders?: number
}

export interface PeriodComparisonData {
	currency?: string
	recent: ComparisonPeriod
	prior: ComparisonPeriod
	change: { revenue_pct: number | null; orders_pct: number | null }
}

export interface RestockPriorityItem {
	site_id: string
	site_name: string
	store_id?: number
	cell_index: number
	slot_label?: string
	product_name?: string
	stock_status: string
	quantity?: number
	revenue_cents?: number
	units?: number
	navigate?: { path: string; label: string }
}

export interface StoreVisitLeg {
	duration_seconds: number
	distance_meters: number
	estimated?: boolean
}

export interface StoreVisitRoute {
	located_stops?: number
	unlocated_stops?: number
	total_duration_seconds?: number
	total_distance_meters?: number
	estimated?: boolean
	polyline?: string
}

export interface StoreVisitStop {
	order: number
	site_id: string
	site_name: string
	store_id?: number
	store_name?: string
	score: number
	reasons: string[]
	navigate?: { path: string; label: string }
	latitude?: number
	longitude?: number
	leg?: StoreVisitLeg
}

export interface PendingPlanogramData {
	site_id: string
	site_name?: string
	store_id?: number
	has_pending: boolean
	pending_planogram_layout_id?: number
	pending_approval_status?: string
	change_count?: number
	note?: string
	navigate?: { path: string; label: string }
}

export interface VisionRiskData {
	site_id: string
	site_name?: string
	warning_count: number
	warnings: Array<Record<string, unknown>>
	note?: string
}

export interface PaymentFailuresData {
	from: string
	to: string
	total_failed: number
	by_site: Array<{ site_id: string; site_name?: string; store_id?: number; failed_count: number }>
	navigate?: { path: string; label: string }
}

export interface ProductPlacementData {
	product_id: number
	period: { from: string; to: string }
	top_cabinets: Array<Record<string, unknown>>
	pair_suggestions: Array<Record<string, unknown>>
}

export interface CabinetComparisonData {
	store_id: number
	from: string
	to: string
	currency?: string
	cabinets: Array<{
		site_id: string
		site_name?: string
		revenue_dollars?: number
		orders?: number
		fill_rate?: number | null
	}>
}

export interface StorePairMapData {
	store_id: number
	from: string
	to: string
	pairs: Array<Record<string, unknown>>
	cabinets?: Array<{ site_id: string; site_name?: string }>
}

export interface PromotionPerformanceItem {
	voucher_id: number
	name?: string
	product_name?: string | null
	discounted_orders?: number
	total_discount_dollars?: number
	navigate?: { path: string; label: string }
}

export interface PlanogramSwapItem {
	cell_index: number
	slot_label?: string | null
	current_product_name?: string | null
	suggested_product_name?: string | null
	current_side_photo?: string | null
	suggested_side_photo?: string | null
	current_revenue_cents?: number | null
	suggested_revenue_cents?: number | null
	current_units?: number | null
	rationale?: string | null
}

export interface PlanogramSwapsData {
	site_id: string
	store_id?: number
	currency?: string
	swaps: PlanogramSwapItem[]
	analysis?: { cells_analyzed: number }
	note?: string
}

export interface PromotionPerformanceData {
	days: number
	items: PromotionPerformanceItem[]
	note?: string
}

export interface RestockPrioritiesData {
	items: RestockPriorityItem[]
	count: number
}

export interface PromotionSuggestionItem {
	product_id: number
	product_name: string | null
	kind: 'slow_mover' | 'basket_pair'
	suggested_discount_percent: number
	reason: string
	evidence?: Record<string, unknown>
	image?: string | null
	end_date: string
	suggested_store_id?: number | null
	suggested_store_name?: string | null
}

export interface PromotionSuggestionsData {
	scope?: { operator_id?: number | null; store_id?: number | null }
	from?: string
	to?: string
	currency?: string | null
	stores: Array<{ id: number; name: string }>
	suggestions: PromotionSuggestionItem[]
	note?: string
}

export interface StoreVisitPlanData {
	store_id?: number | null
	fleet_wide?: boolean
	stops: StoreVisitStop[]
	route?: StoreVisitRoute
	summary?: string
}

export type AssistantWidget = { id: string } & (
	| { type: 'period_comparison'; data: PeriodComparisonData }
	| { type: 'restock_priorities'; data: RestockPrioritiesData }
	| { type: 'store_visit_plan'; data: StoreVisitPlanData }
	| { type: 'pending_planogram'; data: PendingPlanogramData }
	| { type: 'vision_risk'; data: VisionRiskData }
	| { type: 'payment_failures'; data: PaymentFailuresData }
	| { type: 'product_placement'; data: ProductPlacementData }
	| { type: 'cabinet_comparison'; data: CabinetComparisonData }
	| { type: 'store_pair_map'; data: StorePairMapData }
	| { type: 'promotion_performance'; data: PromotionPerformanceData }
	| { type: 'planogram_swaps'; data: PlanogramSwapsData }
	| { type: 'promotion_suggestions'; data: PromotionSuggestionsData }
)

const WIDGET_FLAGS: Array<{ flag: string; type: AssistantWidget['type']; tool: string }> = [
	{ flag: '__period_comparison_widget', type: 'period_comparison', tool: 'get_period_comparison' },
	{ flag: '__restock_priorities_widget', type: 'restock_priorities', tool: 'get_restock_priorities' },
	{ flag: '__store_visit_plan_widget', type: 'store_visit_plan', tool: 'get_store_visit_plan' },
	{ flag: '__pending_planogram_review_widget', type: 'pending_planogram', tool: 'get_pending_planogram_review' },
	{ flag: '__vision_risk_widget', type: 'vision_risk', tool: 'get_vision_risk_summary' },
	{ flag: '__payment_failures_widget', type: 'payment_failures', tool: 'get_payment_failures_summary' },
	{ flag: '__product_placement_widget', type: 'product_placement', tool: 'get_product_placement_recommendations' },
	{ flag: '__cabinet_comparison_widget', type: 'cabinet_comparison', tool: 'get_store_cabinet_comparison' },
	{ flag: '__store_pair_map_widget', type: 'store_pair_map', tool: 'get_store_pair_placement_map' },
	{ flag: '__promotion_performance_widget', type: 'promotion_performance', tool: 'get_promotion_performance' },
	{ flag: '__planogram_swaps_widget', type: 'planogram_swaps', tool: 'suggest_planogram_swaps' },
	{ flag: '__promotion_suggestions_widget', type: 'promotion_suggestions', tool: 'suggest_promotions' },
]

export function extractAssistantWidgetsFromToolCalls(toolCalls: ToolCallLike[] | undefined): AssistantWidget[] {
	if (!toolCalls?.length) return []
	const widgets: AssistantWidget[] = []

	for (const tc of toolCalls) {
		if (tc.status !== 'done' || !tc.result) continue
		const parsed = parseToolJson(tc.result)
		if (!parsed) continue

		for (const spec of WIDGET_FLAGS) {
			if (tc.name !== spec.tool || !parsed[spec.flag]) continue
			widgets.push({ id: tc.id ?? `${spec.type}-${widgets.length}`, type: spec.type, data: parsed as never })
			break
		}
	}

	return widgets
}
